import { useState } from 'react';
import { Screen } from '../../app/Frame';
import { useMeContext } from '../../app/MeContext';
import { useScreen } from '../../app/shell-context';
import { pluralWithCount } from '../../lib/plural';
import { addDaysISO, formatTime, humanISODay, isoDate, localToUtc, todayISO } from '../../lib/time';
import { Pill } from '../../ui/parts';
import { FormSkeleton, LoadFailed } from '../org-parts';
import { useAuditLog } from '../api';
import type { AuditEntry } from '../types';

/**
 * Журнал действий организации.
 *
 * Отвечает на вопрос, который иначе задать некому: кто это поменял. Отдельно
 * отмечен владелец продукта — у него есть доступ в любую организацию, и вход
 * туда не должен выглядеть как работа своего администратора.
 *
 * Строки только читаются: журнал, из которого можно что-то убрать, не журнал.
 */

/**
 * Три среза, три разных вопроса: период — «что менялось на прошлой неделе»,
 * источник — «что тут делал не мой человек», тип — «что происходило с записями».
 *
 * Списком, а не рядами чипов: у типа семь значений, у источника шесть, и тремя
 * рядами чипов журнал уезжал бы за нижний край ещё до первой записи.
 */
const PERIODS: { value: string; label: string; days: number | null }[] = [
  { value: '', label: 'Всё время', days: null },
  { value: 'today', label: 'Сегодня', days: 1 },
  { value: '7', label: 'Последние 7 дней', days: 7 },
  { value: '30', label: 'Последние 30 дней', days: 30 },
];

const SOURCES: { value: string; label: string }[] = [
  { value: '', label: 'Любой' },
  { value: 'superadmin', label: 'Владелец' },
  { value: 'admin', label: 'Администратор' },
  { value: 'specialist', label: 'Специалист' },
  { value: 'client', label: 'Клиент' },
  { value: 'system', label: 'Система' },
];

/**
 * Подписи типов — дело интерфейса, а состав каждого типа знает сервер: он
 * раскладывает действия по семействам приставки (`auditTypeOf`). Экран
 * передаёт только код, поэтому новое действие попадает в фильтр само.
 */
const TYPES: { value: string; label: string }[] = [
  { value: '', label: 'Любой' },
  { value: 'appointments', label: 'Записи и оплаты' },
  { value: 'clients', label: 'Клиенты' },
  { value: 'staff', label: 'Специалисты' },
  { value: 'services', label: 'Услуги и категории' },
  { value: 'settings', label: 'Настройки организации' },
  { value: 'access', label: 'Доступ и права' },
];

/**
 * Роль в бейдже — коротким словом и с большой буквы: это метка, а не часть
 * фразы. Длинное «владелец продукта» внутри карточки не употребляем — оно
 * повторяло бы то, что и так стоит в бейдже, и удлиняло строку втрое.
 */
const ROLE_LABEL: Record<string, string> = {
  superadmin: 'Владелец',
  admin: 'Администратор',
  specialist: 'Специалист',
  client: 'Клиент',
  system: 'Система',
};

export function OrgAudit() {
  const me = useMeContext();
  const tz = me.center.tz;
  const [period, setPeriod] = useState('');
  const [role, setRole] = useState('');
  const [type, setType] = useState('');

  // Границу периода считаем в поясе организации: «сегодня» у неё своё, и
  // полночь по часам того, кто смотрит, отрезала бы не тот кусок журнала.
  const days = PERIODS.find((p) => p.value === period)?.days ?? null;
  const from = days === null ? null : localToUtc(addDaysISO(todayISO(tz), -(days - 1)), 0, tz);
  const log = useAuditLog({ role, type, from });

  useScreen('Журнал действий', null);

  const pages = log.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);
  const total = pages[0]?.total ?? 0;
  const filtered = !!period || !!role || !!type;

  const reset = () => { setPeriod(''); setRole(''); setType(''); };

  return (
    <Screen>
      <p className="lead">
        Здесь отображаются изменения и административные действия в организации. Действия
        владельца продукта также фиксируются в журнале.
      </p>

      <div className="kv kv-wrap">
        <label className="kv-row">
          <span className="k">Период</span>
          <span className="v">
            <select aria-label="Период" value={period} onChange={(e) => setPeriod(e.target.value)}>
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </span>
        </label>
        <label className="kv-row">
          <span className="k">Источник</span>
          <span className="v">
            <select aria-label="Источник" value={role} onChange={(e) => setRole(e.target.value)}>
              {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </span>
        </label>
        <label className="kv-row">
          <span className="k">Тип события</span>
          <span className="v">
            <select aria-label="Тип события" value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </span>
        </label>
      </div>

      {/* Сброс появляется только когда есть что сбрасывать: иначе кнопка висит
          рядом с полным списком и обещает действие, которого не будет. */}
      {filtered ? (
        <div className="danger-zone">
          <button className="linkish" type="button" onClick={reset}>Сбросить фильтры</button>
        </div>
      ) : null}

      {log.isError ? (
        <LoadFailed busy={log.isFetching} onRetry={() => void log.refetch()} />
      ) : log.isPending ? (
        <FormSkeleton rows={4} />
      ) : items.length === 0 ? (
        <p className="lead">
          {filtered ? 'По этим фильтрам записей нет.' : 'Записей пока нет — в организации ещё ничего не меняли.'}
        </p>
      ) : (
        <>
          {groupByDay(items, me.center.tz).map(([day, rows]) => (
            <div key={day}>
              <p className="eyebrow">{humanISODay(day, me.center.tz)}</p>
              <div className="rows" style={{ marginTop: 8 }}>
                {rows.map((e) => <Entry key={e.id} entry={e} />)}
              </div>
            </div>
          ))}

          {log.hasNextPage ? (
            <button
              className="add"
              disabled={log.isFetchingNextPage}
              onClick={() => void log.fetchNextPage()}
            >
              {log.isFetchingNextPage ? 'Загружаем…' : `Показать ещё · осталось ${total - items.length}`}
            </button>
          ) : (
            <p className="footnote" style={{ textAlign: 'left' }}>
              Это всё, что записано: {pluralWithCount(total, 'запись', 'записи', 'записей')}.
            </p>
          )}
        </>
      )}
    </Screen>
  );
}

/**
 * Карточка записи. Каждая строка отвечает на свой вопрос, и ни один ответ не
 * повторяется дважды:
 *
 *   заголовок — что произошло;
 *   строка автора — кто и когда;
 *   подробности — что именно изменилось;
 *   бейдж — с какой ролью действие сделано.
 *
 * Раньше роль стояла во всех трёх местах сразу: «Вход владельца продукта»,
 * «Nick · владелец продукта» и бейдж «Владелец» — три раза об одном в карточке
 * высотой в три строки.
 */
function Entry({ entry }: { entry: AuditEntry }) {
  const me = useMeContext();
  const time = formatTime(entry.at, me.center.tz, me.center.timeFormat);
  const role = ROLE_LABEL[entry.role] ?? entry.role;
  return (
    <div className="rw">
      <span className="rw-body">
        <b>{entry.title}</b>
        {/* Имени может не быть: у старых записей и у действий по служебному
            токену. Тогда остаётся время — роль всё равно стоит в бейдже. */}
        <span>{entry.actorName ? `${time} · ${entry.actorName}` : time}</span>
        {/* Подробности — отдельной строкой: в одну ленту с временем и именем
            они читаются как продолжение подписи, а не как «что произошло». */}
        {entry.detail ? <span>{entry.detail}</span> : null}
      </span>
      {/* Владелец продукта помечен заметнее остальных: у него есть доступ в любую
          организацию, и его вход не должен теряться среди работы своих людей.
          Остальные роли — нейтральной пилюлей: бейдж поясняет строку, а не спорит
          с заголовком за внимание. */}
      <Pill kind={entry.byOwner ? 'await' : 'done'}>{role}</Pill>
    </div>
  );
}

/** Записи по дням: сплошная лента из полусотни строк не читается. */
function groupByDay(items: AuditEntry[], tz: string): [string, AuditEntry[]][] {
  const out: [string, AuditEntry[]][] = [];
  for (const e of items) {
    const day = isoDate(e.at, tz);
    const last = out[out.length - 1];
    if (last && last[0] === day) last[1].push(e);
    else out.push([day, [e]]);
  }
  return out;
}
